import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// Monitor de rendimiento para salas que dejan la web abierta muchas horas.
//   - Activarlo: abrir cualquier pantalla con ?perf=1 (se recuerda en esta pestaña). Quitarlo: ?perf=0.
//   - Muestra FPS, peor cuadro, memoria JS (solo Chrome/Edge), nodos del DOM, tareas largas y tiempo abierta.
//   - Guarda una muestra por minuto en window.__perfLog (hasta ~16 h). Para copiarla desde la consola:
//       copy(JSON.stringify(window.__perfLog))
//   Si la memoria o los nodos del DOM sólo suben con las horas, hay una fuga; si los FPS bajan, hay carga de dibujo.
export const PERF_STORAGE_KEY = 'perf-hud';
const SAMPLE_EVERY_MS = 60000;
const MAX_SAMPLES = 1000;

export const isPerfEnabled = () => {
  const param = new URLSearchParams(window.location.search).get('perf');
  try {
    if (param === '1') window.sessionStorage.setItem(PERF_STORAGE_KEY, '1');
    if (param === '0') window.sessionStorage.removeItem(PERF_STORAGE_KEY);
    return window.sessionStorage.getItem(PERF_STORAGE_KEY) === '1';
  } catch {
    return param === '1';
  }
};

const Panel = styled.div`
  position: fixed;
  left: 8px;
  bottom: 8px;
  z-index: 2147483647;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.78);
  color: #b8ffb8;
  font: 11px/1.45 Consolas, Menlo, monospace;
  pointer-events: none;
  white-space: pre;
`;

const heapMb = () => (performance.memory ? performance.memory.usedJSHeapSize / 1048576 : null);

export function usePerformanceStats(enabled) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const started = performance.now();
    let frames = 0, worst = 0, last = performance.now(), windowStart = last, raf = 0, longTasks = 0;
    let fps = 0, worstMs = 0, baseline = null, lastSample = started;
    window.__perfLog = window.__perfLog || [];
    let observer;
    try {
      observer = new PerformanceObserver((list) => { longTasks += list.getEntries().length; });
      observer.observe({ entryTypes: ['longtask'] });
    } catch {
      // longtask no está disponible en todos los navegadores
    }
    const tick = (now) => {
      const delta = now - last;
      last = now;
      if (delta < 1000) { // un salto largo es la pestaña en segundo plano, no un cuadro lento
        frames += 1;
        worst = Math.max(worst, delta);
      }
      if (now - windowStart >= 1000) {
        fps = (frames * 1000) / (now - windowStart);
        worstMs = worst;
        frames = 0; worst = 0; windowStart = now;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const report = setInterval(() => {
      const now = performance.now();
      const heap = heapMb();
      if (baseline === null && heap !== null && now - started > SAMPLE_EVERY_MS) baseline = heap;
      const nodes = document.getElementsByTagName('*').length;
      setStats({ fps, worstMs, heap, growth: baseline !== null && heap !== null ? heap - baseline : null, nodes, longTasks, minutes: (now - started) / 60000 });
      if (now - lastSample >= SAMPLE_EVERY_MS) {
        lastSample = now;
        window.__perfLog.push({ min: +((now - started) / 60000).toFixed(1), fps: +fps.toFixed(1), worstMs: +worstMs.toFixed(1), heapMB: heap === null ? null : +heap.toFixed(1), nodes, longTasks });
        if (window.__perfLog.length > MAX_SAMPLES) window.__perfLog.shift();
      }
    }, 2000);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(report);
      observer?.disconnect();
    };
  }, [enabled]);
  return stats;
}

export default function PerformanceMonitor() {
  const [enabled] = useState(isPerfEnabled);
  const stats = usePerformanceStats(enabled);
  if (!enabled) return null;
  const line = (label, value) => `${label.padEnd(16)}${value}`;
  const hours = stats ? Math.floor(stats.minutes / 60) : 0;
  const text = stats
    ? [
        line('FPS', stats.fps.toFixed(0)),
        line('Peor cuadro', `${stats.worstMs.toFixed(0)} ms`),
        line('Memoria JS', stats.heap === null ? 'no disponible' : `${stats.heap.toFixed(0)} MB`),
        line('Desde 1 min', stats.growth === null ? '—' : `${stats.growth >= 0 ? '+' : ''}${stats.growth.toFixed(1)} MB`),
        line('Nodos DOM', String(stats.nodes)),
        line('Tareas largas', String(stats.longTasks)),
        line('Abierta', `${hours} h ${Math.floor(stats.minutes % 60)} min`),
      ].join('\n')
    : 'Midiendo…';
  return (
    <Panel role="status" aria-label="Monitor de rendimiento" data-testid="performance-monitor">
      {text}
    </Panel>
  );
}
